import { useEffect, useState } from 'react';
import { Alert, Box, CircularProgress, Stack, Typography } from '@mui/material';
import { useLocation, useParams } from 'react-router-dom';
import api from '../../api/appPublicApi';
import { shelfTagOptions } from './shelfTags.mjs';
import { createShelfTagPdf, navigateToShelfTagPdf } from './shelfTagPdf.mjs';
import { readShelfTagRequest, shelfTagProduct } from './shelfTagEmbed.mjs';

export default function ItemShelfTags() {
  const { itemNumber } = useParams();
  const { search } = useLocation();
  const [error, setError] = useState('');

  useEffect(() => {
    const controller = new AbortController();
    setError('');
    const generate = async () => {
      if (!/^\d{1,30}$/.test(itemNumber || '')) {
        setError('Enter a valid item number in the URL.');
        return;
      }
      let data;
      try {
        ({ data } = await api.get(`/api/app/itemSpecSheet/${encodeURIComponent(itemNumber)}`, { signal: controller.signal }));
      } catch (failure) {
        if (!controller.signal.aborted) setError('Unable to load this item. Reload the page to try again.');
        return;
      }
      if (controller.signal.aborted) return;
      const product = shelfTagProduct(data, itemNumber);
      if (!product) {
        setError(`Invalid Product Number: ${itemNumber}`);
        return;
      }
      const options = { product, ...readShelfTagRequest(search) };
      const { errors } = shelfTagOptions(options);
      if (Object.keys(errors).length) {
        setError(Object.values(errors).join(' '));
        return;
      }
      try {
        const blob = await createShelfTagPdf(options);
        // Ignore work from an earlier URL or StrictMode's discarded effect.
        if (!controller.signal.aborted) navigateToShelfTagPdf(blob);
      } catch (failure) {
        if (!controller.signal.aborted) setError(failure.message || 'Labels could not be generated. Reload the page to try again.');
      }
    };
    generate();
    return () => controller.abort();
  }, [itemNumber, search]);

  return <Box sx={{ p: 3, maxWidth: 580, mx: 'auto' }}>
    {error ? <Alert severity='error'>{error}</Alert> : <Stack direction='row' alignItems='center' spacing={2} role='status'>
      <CircularProgress size={24} />
      <Typography>Generating shelf tags…</Typography>
    </Stack>}
  </Box>;
}
